import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton, StatusBadge, Badge, CopyLinkButton } from '../components/primitives';
import { ConfirmModal } from '../components/Modal';
import type { FormItem, SectionInfo } from '../types';
import { useAdmin } from './context';
import { FormEditor } from './FormEditor';
import { SectionEditor } from './SectionEditor';
import type { MutationResult } from '../api/client';

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface DashboardData {
  forms: FormItem[];
  sections: SectionInfo[];
}

export function Dashboard() {
  const { api, me } = useAdmin();
  const canEdit = me.role === 'admin' || me.role === 'master';

  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ open: boolean; form: FormItem | null }>({ open: false, form: null });
  const [sectionEditor, setSectionEditor] = useState<{ open: boolean; section: SectionInfo | null }>({
    open: false,
    section: null,
  });
  /** set after creating a section from within the form editor — preselects it there */
  const [newSectionKey, setNewSectionKey] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<FormItem | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.listForms());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load forms');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const flash = (msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice(null), 8000);
  };

  const onFormSaved = (result: MutationResult) => {
    setEditor({ open: false, form: null });
    flash(result.publishWarning ?? `Saved ${result.form.slug} — the public site is updated.`);
    void load();
  };

  const onSectionSaved = (section: SectionInfo, isNew: boolean) => {
    setSectionEditor({ open: false, section: null });
    flash(isNew ? `Section "${section.label}" created — it now appears when you add a form.` : `Section "${section.label}" updated.`);
    if (isNew) setNewSectionKey(section.key);
    void load();
  };

  const onSectionDeleted = (key: string) => {
    setSectionEditor({ open: false, section: null });
    flash(`Section "${key}" deleted.`);
    void load();
  };

  const archive = async () => {
    if (!archiving) return;
    setBusy(true);
    try {
      await api.archiveForm(archiving.slug);
      flash(`Archived ${archiving.slug} — its public link now shows a friendly "no longer available" page.`);
      setArchiving(null);
      void load();
    } catch (err) {
      flash(err instanceof Error ? err.message : 'Archive failed');
    } finally {
      setBusy(false);
    }
  };

  const formsInSection = (key: string) => (data?.forms ?? []).filter((f) => f.section === key);

  // Sections from the table, in API order (label-sorted), plus any stray
  // section keys that only exist on forms (legacy safety net).
  const sectionsToRender: SectionInfo[] = data
    ? [
        ...data.sections,
        ...[...new Set(data.forms.map((f) => f.section))]
          .filter((key) => !data.sections.some((s) => s.key === key))
          .map((key) => ({ key, label: key })),
      ]
    : [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Forms</h1>
          <p className="mt-1 text-sm text-muted">
            {canEdit
              ? 'Create, edit and publish the forms on forms.knocking.com.'
              : 'All forms on forms.knocking.com. You have read-only access — an admin can make changes.'}
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSectionEditor({ open: true, section: null })}
              className="rounded-full border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-input"
            >
              + New section
            </button>
            <button
              type="button"
              onClick={() => setEditor({ open: true, form: null })}
              className="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-contrast transition-colors hover:bg-brand-strong"
            >
              + New form
            </button>
          </div>
        )}
      </div>

      {notice && (
        <p role="status" className="mt-4 rounded-xl border border-brand/40 bg-brand/10 px-4 py-2.5 text-sm">
          {notice}
        </p>
      )}
      {error && (
        <div role="alert" className="mt-4 rounded-xl border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm">
          {error}{' '}
          <button type="button" onClick={() => void load()} className="font-medium underline underline-offset-2">
            Retry
          </button>
        </div>
      )}

      {!data && !error && (
        <div className="mt-8 flex flex-col gap-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      )}

      {data && data.forms.length === 0 && data.sections.length === 0 && (
        <p className="mt-8 rounded-2xl border border-border bg-card px-6 py-12 text-center text-sm text-muted">
          No forms yet. {canEdit ? 'Create the first one with “+ New form”.' : 'Check back later.'}
        </p>
      )}

      {data &&
        sectionsToRender.map((section) => {
          const list = formsInSection(section.key);
          return (
            <section key={section.key} aria-labelledby={`sec-${section.key}`} className="mt-10">
              <div className="flex items-center gap-2">
                <h2 id={`sec-${section.key}`} className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">
                  {section.label}
                  <span className="ml-2 font-normal">({list.length})</span>
                </h2>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setSectionEditor({ open: true, section })}
                    aria-label={`Edit section ${section.label}`}
                    title="Edit section"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted transition-colors hover:bg-input hover:text-foreground"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    </svg>
                  </button>
                )}
              </div>
              {list.length === 0 ? (
                <p className="mt-3 rounded-2xl border border-dashed border-border px-5 py-6 text-center text-xs text-muted">
                  No forms in this section yet.
                </p>
              ) : (
                <ul className="mt-3 flex flex-col gap-3">
                  {list.map((form) => (
                    <li
                      key={form.slug}
                      className="flex flex-col gap-4 rounded-2xl border border-border bg-card px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{form.displayName}</span>
                          <StatusBadge status={form.status} />
                          <Badge tone="muted">{form.provider}</Badge>
                        </div>
                        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted" title={form.embedUrl}>
                          <code className="font-mono text-xs">/{form.slug}</code>
                          <CopyLinkButton
                            text={`${window.location.origin}/${form.slug}`}
                            label={`Copy public link for ${form.displayName}`}
                          />
                          <span className="truncate">
                            · updated by {form.updatedBy} {relTime(form.updatedAt)}
                          </span>
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {form.status === 'published' && (
                          <Link
                            to={`/${form.slug}`}
                            target="_blank"
                            className="rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-input"
                          >
                            Visit ↗
                          </Link>
                        )}
                        {canEdit && (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditor({ open: true, form })}
                              className="rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-input"
                            >
                              Edit
                            </button>
                            {form.status !== 'archived' && (
                              <button
                                type="button"
                                onClick={() => setArchiving(form)}
                                className="rounded-full border border-danger/40 px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger/10"
                              >
                                Archive
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}

      {editor.open && (
        <FormEditor
          form={editor.form}
          sections={data?.sections ?? []}
          newSectionKey={newSectionKey}
          onNewSection={() => setSectionEditor({ open: true, section: null })}
          onClose={() => {
            setEditor({ open: false, form: null });
            setNewSectionKey(null);
          }}
          onSaved={onFormSaved}
        />
      )}

      {sectionEditor.open && (
        <SectionEditor
          section={sectionEditor.section}
          formsCount={sectionEditor.section ? formsInSection(sectionEditor.section.key).length : 0}
          onClose={() => setSectionEditor({ open: false, section: null })}
          onSaved={onSectionSaved}
          onDeleted={onSectionDeleted}
        />
      )}

      {archiving && (
        <ConfirmModal
          title={`Archive ${archiving.displayName}?`}
          message={
            <>
              Archiving removes <code className="font-mono">/{archiving.slug}</code> from the public site
              immediately — visitors will see a friendly “no longer available” page. The form record and its
              audit history are kept, and you can re-publish it any time by setting the status back.
            </>
          }
          confirmLabel="Archive form"
          danger
          busy={busy}
          onConfirm={() => void archive()}
          onClose={() => setArchiving(null)}
        />
      )}
    </div>
  );
}
